import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import Button from "../Button/Button"
import { showSnackBar } from "../Snackbar/Snackbar"
import { fetchBrandGrants, grantBrand, revokeBrand } from "../../features/company/brandGrants"

// Which brands a staff account may act as.
//
// Only the brands the CALLER holds are offered, because that is the rule the server enforces: you
// may hand out a firm you have access to yourself and no other. Rendering every brand and letting
// the server refuse would teach the screen to show controls that do not work.
//
// The account's home firm is shown and NOT removable — it is not part of the grant set, so a control
// for it would report success and change nothing.
const BrandAccess = ({ userId, myBrands = [], homeCompany }) => {
  const dispatch = useDispatch()
  const [granted, setGranted] = useState([])
  const [busy, setBusy] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = async () => {
    const response = await dispatch(fetchBrandGrants(userId))

    if (response?.error) {
      showSnackBar('error', typeof response.payload === 'string' ? response.payload : 'Failed to load brand access')
      setLoading(false)
      return
    }

    setGranted(response?.payload?.data || [])
    setLoading(false)
  }

  useEffect(() => {
    if (userId) load()
  }, [userId])

  const toggle = async (companyId, isGranted) => {
    setBusy(companyId)

    const response = isGranted
      ? await dispatch(revokeBrand({ userId, companyId }))
      : await dispatch(grantBrand({ userId, companyId }))

    setBusy(null)

    if (response?.error) {
      showSnackBar('error', typeof response.payload === 'string' ? response.payload : 'Could not change brand access')
      return
    }

    setGranted(response?.payload?.data || [])
    showSnackBar('success', isGranted ? 'Brand access withdrawn' : 'Brand access granted')
  }

  if (!userId) return null

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-text-primary mb-1">Brand access</h3>
      <p className="text-sm text-text-secondary mb-4">
        Which brands this account can work in. You can only give out a brand you have access to yourself.
      </p>

      {loading && <div className="text-text-secondary">Loading...</div>}

      {!loading && myBrands.length === 0 && (
        <div className="text-text-secondary">You have access to one brand, so there is nothing to hand out.</div>
      )}

      {!loading && myBrands.map((brand) => {
        const isGranted = granted.includes(brand._id)
        const isHome = String(brand._id) === String(homeCompany)

        return (
          <div key={brand._id} className="flex items-center justify-between py-2 border-b border-divider/20">
            <div>
              <div className="font-medium text-text-primary">{brand.title || 'Unnamed brand'}</div>
              {isHome && <div className="text-xs text-text-secondary">This account&apos;s own brand</div>}
            </div>
            {isHome
              ? <span className="text-sm text-text-secondary">Always</span>
              : (
                <Button
                  type="outlined"
                  size="sm"
                  onClick={() => toggle(brand._id, isGranted)}
                  disabled={busy === brand._id}
                >
                  {isGranted ? 'Withdraw' : 'Grant'}
                </Button>
              )}
          </div>
        )
      })}
    </div>
  )
}

export default BrandAccess
